import { AlertTriangle, Check, CircleHelp, Minus, X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useState } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import { Alert, AlertDescription } from "../../../../components/ui/alert";
import { Button } from "../../../../components/ui/button";
import { fetchFastApi } from "../../../../api/fastapi";
import { useAudit } from "../../../../contexts/auditContext";
import { useControl } from "../../../../hooks/audit/useControl";
import { useProcedureSession } from "../../../../hooks/audit/useProcedureSession";
import { useProcedureSessionTableShell } from "../../../../hooks/audit/useProcedureSessionTableShell";
import {
  getAttributeTestColumnLabel,
  getColumnDisplayName,
} from "../../../../lib/columnNameUtils";
import {
  analyticsResultIdFromSelection,
  analyticsSummaryToCheckResult,
} from "../../../../models/audit/analyticsTranslator";
import {
  type AuditSession,
  type ProcedureColumn,
  sessionSchema,
} from "../../../../models/audit/auditSession";
import { type ControlSessionEnvelope } from "../../../../models/audit/controlSession";
import { type DrySessionSourceRow } from "../../../../models/audit/procedureSessionTableShell";
import {
  analyticsCellResult,
  type VerdictCellResult,
} from "../lib/verdictCellResult";
import { resolveLiveAnalyticsVerdict } from "../lib/liveAnalyticsVerdict";

/**
 * What a cell badges as: a verdict the grid read, or one of two states where it
 * has no reading to give — `loading` while the fetch carrying analytics verdicts
 * is out, `unavailable` once it has failed.
 *
 * Both are held apart from `inconclusive`, which says something different.
 * Inconclusive is itself a reading of the cell: it was tested, and the verdict
 * standing against it cannot be resolved. A fetch that is out or has failed says
 * nothing about the cell — not even that it ran — so badging it inconclusive
 * would state a finding about the sample that no data backs, which is the
 * misreport the pending dash already makes in the other direction.
 *
 * The two are held apart from each other because only one of them is the grid's
 * final answer: a cell still waiting says so, and a retry is offered for the one
 * that will not resolve on its own.
 */
type CellResult = VerdictCellResult | "loading" | "unavailable";

interface WorkpaperNavGridProps {
  procedureSessionId: string;
  controlSession: ControlSessionEnvelope;
  /** The clickable element the dropdown anchors to (the fold-out header chip). */
  trigger: ReactNode;
  /** Navigate to a sample × column cell — `checkResultId` is the namespaced
   * selection the fold-out re-targets on, `attr-test-id-<id>` over an
   * attribute-test cell and `analytics-id-<id>` over an analytics one. The
   * fold-out forwards it into the shared selection untouched, and the namespace
   * is what tells the detail panels which verdict kind they are opening. */
  onNavigate: (target: { sourceRowId: string; checkResultId: string }) => void;
  /** The currently-open cell, so the grid can mark "you are here". Matched by
   * bare result id (format-independent), with the sample row as a guard.
   *
   * An analytics cell has no equivalent prop: its result id is read off the live
   * selection in context, so both cell kinds mark themselves without the
   * fold-out having to pass one id per kind. */
  currentSourceRowId?: string;
  currentAttributeTestResultId?: string;
}

function cellIcon(result: CellResult) {
  switch (result) {
    case "pass":
      return <Check className="h-3.5 w-3.5 text-emerald-600" strokeWidth={3} />;
    case "fail":
      return <X className="h-3.5 w-3.5 text-red-500" strokeWidth={3} />;
    case "not_applicable":
      return <Minus className="h-3.5 w-3.5 text-stone-400" strokeWidth={3} />;
    case "inconclusive":
      return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
    case "unavailable":
      return <CircleHelp className="h-3.5 w-3.5 text-stone-400" />;
    case "loading":
      return <span className="text-xs text-stone-300">…</span>;
    case "pending":
      return <span className="text-xs text-stone-300">—</span>;
  }
}

/** Why a cell the grid has no reading for has none, since the answer is about
 * the request rather than the sample. `pending` and `inconclusive` are readings
 * of the sample itself and are named by the workpaper cell the dropdown opens,
 * so they keep the bare mark they have there. */
function unreadCellTitle(result: CellResult): string | undefined {
  switch (result) {
    case "loading":
      return "Loading this test's result";
    case "unavailable":
      return "This test's result couldn't be loaded";
    default:
      return undefined;
  }
}

/** A cell's badge, plus the ids the grid navigates and self-marks with: the
 * namespaced `checkResultId` the fold-out selects on, and the bare `resultId`
 * the "you are here" match compares. */
interface CellState {
  result: CellResult;
  checkResultId: string | null;
  resultId: string | null;
}

/** A cell holding no verdict. Both kinds share it: an analytics column that has
 * not run and an attribute test that has not run are the same thing to read. */
const PENDING_CELL: CellState = {
  result: "pending",
  checkResultId: null,
  resultId: null,
};

/** An analytics cell with no single readable verdict. Without an id it renders
 * unclickable and never marks itself current, by the grid's one clickability
 * rule for every kind: a mark navigates only when it names a single result to
 * open, the rule that also leaves an attribute-test result without an id
 * unclickable. Opening a parsed peer instead would show a verdict the workpaper
 * cell deliberately withholds. */
const UNREADABLE_ANALYTICS_CELL: CellState = {
  result: "inconclusive",
  checkResultId: null,
  resultId: null,
};

/** An analytics cell the grid holds no reading for, because the table shell its
 * verdicts come from has failed. Unclickable for the same reason as its peers
 * above: there is no result to open — here because none was received at all. */
const UNAVAILABLE_ANALYTICS_CELL: CellState = {
  result: "unavailable",
  checkResultId: null,
  resultId: null,
};

/** An analytics cell whose shell is still in flight. Distinct from the failed
 * one so a cell that is merely slow is not reported as one that will not
 * arrive, and unclickable for the same reason: it names no result yet. */
const LOADING_ANALYTICS_CELL: CellState = {
  result: "loading",
  checkResultId: null,
  resultId: null,
};

/** Latest attribute-test result for a (sample, attribute-test column) cell, with
 * the bare `resultId` and the `attr-test-id-<id>` selection id when one exists. */
function resolveAttributeTestCell(
  sourceRow: AuditSession["sourceRows"][number],
  column: ProcedureColumn,
): CellState {
  const results = sourceRow.attributeTestResults.filter(
    (r) => r.attributeTestColumnId === column.attributeTestColumn?.id,
  );
  if (results.length === 0) return PENDING_CELL;
  const latest = [...results].sort(
    (a, b) =>
      new Date(b.createdAt ?? 0).getTime() -
      new Date(a.createdAt ?? 0).getTime(),
  )[0];
  const result: CellResult = !latest.isConclusive
    ? "inconclusive"
    : latest.outcome;
  return {
    result,
    checkResultId: latest.id ? `attr-test-id-${latest.id}` : null,
    resultId: latest.id ?? null,
  };
}

/**
 * The live verdict for a (sample, analytics column) cell, badged and minted into
 * the `analytics-id-<id>` selection the fold-out opens on.
 *
 * The selection id comes from the translator, never from a local literal: the
 * prefix a cell selects under has to be the one the detail panels read back, and
 * a second copy of it is how a cell clicks and opens nothing.
 *
 * A cell holding a verdict it cannot read, or more than one, badges as
 * inconclusive rather than pending: it was tested, and the dash would say it was
 * not. The workpaper cell behind this dropdown is the surface that names why.
 */
function resolveAnalyticsCell(
  dryRow: DrySessionSourceRow | undefined,
  column: ProcedureColumn,
): CellState {
  const analyticsColumnId = column.analyticsColumn?.id;
  if (!analyticsColumnId) return PENDING_CELL;
  const live = resolveLiveAnalyticsVerdict(dryRow, analyticsColumnId);
  switch (live.kind) {
    case "none":
      return PENDING_CELL;
    case "unreadable":
      return UNREADABLE_ANALYTICS_CELL;
    case "verdict": {
      const checkResult = analyticsSummaryToCheckResult(live.summary, column);
      return {
        result: analyticsCellResult(checkResult),
        checkResultId: checkResult.id,
        resultId: live.summary.id,
      };
    }
  }
}

/**
 * Where a (sample, analytics column) cell's verdict is read from, or why it
 * cannot be. One request carries every analytics verdict in the grid, so its
 * state is the grid's whole answer for those columns.
 */
type AnalyticsSource =
  | { kind: "rows"; rowsById: Map<string, DrySessionSourceRow> }
  | { kind: "loading" }
  | { kind: "failed" };

/**
 * A cell's badge and ids, dispatched on the kind of column it sits under.
 *
 * The analytics arm cannot delegate a missing shell: `resolveLiveAnalyticsVerdict`
 * reads an absent row as a cell holding nothing, which is how a request still in
 * flight or already failed comes back out as `pending` — a dash over a sample
 * that may well have been tested. Carrying the shell's state in `analytics`
 * rather than in an empty map is what makes those two cases unskippable here.
 *
 * Attribute-test cells are unaffected by any of it: they read off the session
 * hydration, which the shell's state says nothing about.
 */
function resolveCell(
  sourceRow: AuditSession["sourceRows"][number],
  column: ProcedureColumn,
  analytics: AnalyticsSource,
): CellState {
  if (column.attributeTestColumn) {
    return resolveAttributeTestCell(sourceRow, column);
  }
  switch (analytics.kind) {
    case "loading":
      return LOADING_ANALYTICS_CELL;
    case "failed":
      return UNAVAILABLE_ANALYTICS_CELL;
    case "rows":
      return resolveAnalyticsCell(analytics.rowsById.get(sourceRow.id), column);
  }
}

function Grid({
  procedureSessionId,
  session,
  controlSession,
  onNavigate,
  currentSourceRowId,
  currentAttributeTestResultId,
}: {
  procedureSessionId: string;
  session: AuditSession;
  controlSession: ControlSessionEnvelope;
  onNavigate: (target: { sourceRowId: string; checkResultId: string }) => void;
  currentSourceRowId?: string;
  currentAttributeTestResultId?: string;
}) {
  const { data: hydratedControl } = useControl(controlSession.controlId);
  const attributes =
    controlSession.workStep?.attributes ?? hydratedControl?.attributes ?? [];

  // The verdict id behind the open cell when that cell is an analytics one, so
  // the grid marks "you are here" over both kinds while the fold-out keeps
  // passing the one id it has. The selection is the only place an analytics
  // cell's identity is held — nothing hands it down as a prop — and reading it
  // here costs the fold-out no second argument.
  const { currentSelection } = useAudit();
  const currentAnalyticsResultId = analyticsResultIdFromSelection(
    currentSelection?.checkResultId,
  );

  // Analytics verdicts are not on the session hydration: its source rows model
  // attribute-test results only, so the table shell is where a cell's verdict
  // has to be read from. No refresh is staged beside it the way the session's
  // is — an override in the fold-out invalidates this exact entry, so the shell
  // a reopened dropdown reads is already the current one.
  //
  // Its state has to reach the cells, because an absent shell is not a neutral
  // input here: with no rows to read, every analytics cell resolves to the
  // untested dash. Only the analytics columns lose their reading — the
  // attribute-test ones come off the session and stay true — so the grid
  // degrades per column rather than closing over a failure that leaves most of
  // it correct.
  const {
    data: tableShell,
    isLoading: isTableShellLoading,
    error: tableShellError,
    refetch: refetchTableShell,
  } = useProcedureSessionTableShell(procedureSessionId);
  // Rows once there are any, even alongside an error: a refetch that fails
  // leaves the previously fetched shell in the cache, and those verdicts are
  // still the last ones the store gave. A shell-less entry that is neither
  // loading nor errored reads as failed rather than as forever-loading, so the
  // grid always offers the retry that is the only way out of a stuck entry.
  const analyticsSource = useMemo<AnalyticsSource>(() => {
    if (tableShell) {
      return {
        kind: "rows",
        rowsById: new Map(tableShell.sourceRows.map((row) => [row.id, row])),
      };
    }
    return isTableShellLoading ? { kind: "loading" } : { kind: "failed" };
  }, [tableShell, isTableShellLoading]);

  // Both verdict kinds, interleaved in the order the workpaper lays them out:
  // `columnOrder` numbers the procedure's columns as a whole, so a grid that
  // sorted each kind into its own block would read in an order the table the
  // auditor came from never shows.
  const columns = useMemo(
    () =>
      session.procedure.columns
        .filter((col) => col.attributeTestColumn || col.analyticsColumn)
        .sort((a, b) => a.columnOrder - b.columnOrder),
    [session.procedure.columns],
  );

  const rows = useMemo(
    () =>
      [...session.sourceRows].sort(
        (a, b) => (a.sampleNumber ?? 0) - (b.sampleNumber ?? 0),
      ),
    [session.sourceRows],
  );

  if (columns.length === 0) {
    return (
      <p className="px-2 py-3 text-sm text-font-secondary">
        No tested columns found.
      </p>
    );
  }

  // A shell that failed with rows already held keeps those rows in the cells,
  // so the notice says they may be out of date rather than that nothing can be
  // read. Withheld where the procedure has no analytics column, since nothing
  // on screen is then reading off the shell.
  const analyticsUnreadable = analyticsSource.kind === "failed";
  const analyticsStale =
    analyticsSource.kind === "rows" && tableShellError !== null;
  const showAnalyticsFailure =
    (analyticsUnreadable || analyticsStale) &&
    columns.some((col) => col.analyticsColumn);

  return (
    <div className="space-y-2">
      {showAnalyticsFailure ? (
        // Named here rather than left to the provider's toast, which says data
        // failed to load without saying which reading is missing, and is gone
        // by the time the dropdown is reopened. Retry refetches rather than
        // asking for a reopen: a failed entry stays in the cache and
        // `registerQuery` hands the same one back, so only an invalidate
        // clears it.
        //
        // The icon is wrapped because `Alert` lays a direct `svg` child out
        // absolutely for its full-width form; this row is a compact one.
        <Alert className="flex items-center gap-2 rounded-md border-amber-200 bg-amber-50 p-2">
          <span className="flex shrink-0 items-center">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
          </span>
          <AlertDescription className="text-xs text-stone-600">
            {analyticsUnreadable
              ? "Analytics results couldn't be loaded, so those columns can't be read."
              : "Analytics results couldn't be refreshed, so those columns may be out of date."}
          </AlertDescription>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void refetchTableShell()}
          >
            Try again
          </Button>
        </Alert>
      ) : null}
      <table className="border-collapse text-sm">
        <thead>
          <tr>
            <th aria-hidden />
            {/* The group header spans both kinds, so it names what they have in
              common. An analytics column reaches a verdict without belonging to
              an attribute, and "Attribute" over that column is the same
              mislabelling the per-column headers below avoid. */}
            <th
              colSpan={columns.length}
              className="pb-1.5 text-center text-[11px] font-semibold uppercase tracking-wider text-font-secondary"
            >
              Test
            </th>
          </tr>
          <tr>
            <th className="sticky left-0 z-10 border border-stone-200 bg-white px-2 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-font-secondary">
              #
            </th>
            {columns.map((col) => {
              // An attribute-test column's header is "<column code> - <name>",
              // e.g. "D.1 - Config roles present". The code disambiguates columns
              // that share an attribute reference; the label helper strips a
              // name-embedded reference so it isn't doubled. An analytics column
              // carries no attribute and so no code — it names itself from its own
              // name or the workflow bound to it, the same name the fold-out
              // header gives it, because calling it an attribute names a thing it
              // is not.
              const label = col.attributeTestColumn
                ? getAttributeTestColumnLabel(
                    col,
                    session.procedure.columns,
                    attributes,
                  )
                : getColumnDisplayName(
                    col,
                    session.procedure.columns,
                    attributes,
                  );
              return (
                <th
                  key={col.id}
                  className="border border-stone-200 px-2 py-1.5 text-center text-[11px] font-medium text-stone-600"
                  title={label}
                >
                  <span className="mx-auto block max-w-[9rem] truncate">
                    {label}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isCurrentRow = row.id === currentSourceRowId;
            return (
              <tr key={row.id}>
                <td className="sticky left-0 z-10 border border-stone-200 bg-white px-2 text-center text-xs font-medium text-stone-500">
                  {row.sampleNumber ?? "—"}
                </td>
                {columns.map((col) => {
                  const { result, checkResultId, resultId } = resolveCell(
                    row,
                    col,
                    analyticsSource,
                  );
                  // Each kind's id is compared against the selection that names
                  // that kind: an analytics verdict id and an attribute-test
                  // result id are drawn from different tables, so a bare match
                  // across both could mark a cell the auditor is not in.
                  const isCurrentCell =
                    isCurrentRow &&
                    resultId != null &&
                    resultId ===
                      (col.attributeTestColumn
                        ? currentAttributeTestResultId
                        : currentAnalyticsResultId);
                  return (
                    <td key={col.id} className="border border-stone-200 p-0">
                      {checkResultId ? (
                        <button
                          type="button"
                          onClick={() =>
                            onNavigate({ sourceRowId: row.id, checkResultId })
                          }
                          className={`flex h-8 w-full min-w-[2.25rem] items-center justify-center transition-colors ${
                            isCurrentCell
                              ? "bg-blue-50 ring-1 ring-inset ring-blue-400 hover:bg-blue-100"
                              : "hover:bg-stone-100"
                          }`}
                          title={
                            isCurrentCell
                              ? "Current sample / attribute"
                              : "Open this sample / attribute"
                          }
                        >
                          {cellIcon(result)}
                        </button>
                      ) : (
                        <span
                          className="flex h-8 w-full min-w-[2.25rem] items-center justify-center"
                          title={unreadCellTitle(result)}
                        >
                          {cellIcon(result)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Samples (rows) × verdict columns (columns) navigation grid, shown as a compact
 * dropdown anchored below the fold-out header. Attribute-test and analytics
 * columns list together in `columnOrder`, both badged in the one vocabulary the
 * workpaper already speaks. Only result cells are interactive — each highlights
 * on hover and re-targets the fold-out to that sample/column on click, then
 * closes.
 */
export function WorkpaperNavGrid({
  procedureSessionId,
  controlSession,
  trigger,
  onNavigate,
  currentSourceRowId,
  currentAttributeTestResultId,
}: WorkpaperNavGridProps) {
  const [open, setOpen] = useState(false);
  // Instant display from the shared cache (read-only — never invalidate it:
  // the bare `["procedure-session", id]` entry backs the whole audit view, so
  // invalidating would flip the entire page into a loading state).
  const { data: cachedSession, isLoading } = useProcedureSession(
    open ? procedureSessionId : undefined,
  );

  // The cached hydration goes stale because per-cell outcome/status edits in
  // the fold-out mutate a *different* (per-result) entry, so the grid's ✓/✗
  // marks would lag until a full reload. On open, fetch a fresh copy into
  // LOCAL state that overrides the cache once it lands — isolated, so it
  // refreshes the grid without disturbing any other consumer.
  const [freshSession, setFreshSession] = useState<AuditSession | null>(null);
  useEffect(() => {
    if (!open) {
      setFreshSession(null);
      return;
    }
    let cancelled = false;
    fetchFastApi({
      method: "GET",
      path: `/controls/v2/procedure-session/${procedureSessionId}`,
    })
      .then((response) => {
        if (!cancelled) setFreshSession(sessionSchema.parse(response));
      })
      .catch((error) => {
        console.error("Error refreshing nav grid:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [open, procedureSessionId]);

  const session = freshSession ?? cachedSession;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="max-h-[75vh] w-auto min-w-[16rem] max-w-[92vw] overflow-auto p-2"
      >
        {isLoading || !session ? (
          <div className="flex items-center justify-center py-6">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex items-stretch gap-1">
            <span className="flex items-center justify-center rotate-180 text-[11px] font-semibold uppercase tracking-wider text-font-secondary [writing-mode:vertical-rl]">
              Sample
            </span>
            <Grid
              procedureSessionId={procedureSessionId}
              session={session}
              controlSession={controlSession}
              onNavigate={(t) => {
                onNavigate(t);
                setOpen(false);
              }}
              currentSourceRowId={currentSourceRowId}
              currentAttributeTestResultId={currentAttributeTestResultId}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
